import { DatePipe } from "@angular/common"
import { Component, ElementRef, Input } from "@angular/core"
import { BsModalService, ModalOptions } from "ngx-bootstrap/modal"
import { Observable, Subscription } from "rxjs"
import { first } from "rxjs/operators"
import { AppState, RoleMgr } from "../../../../core"
import { ScoutPath, ScoutPoint, TaskStatus } from "../../../../core/models"
import { ScoutTaskDoc } from "../../../../core/schema/scout-task-schema"
import { TableViewComponentBase, TableViewQueryRequest } from "../../../../shared/components/table-view/table-view-component-base"
import { ScoutPointRefDialogComponent } from "../../../scout-point/scout-point-ref-dialog/scout-point-ref-dialog.component"
import * as XLSX from 'xlsx';
import { getMatcherForPattern } from "../../../../shared/directives/filter-field.directive"
import { NumericCompareFn, SortCompareFn } from "../../../../shared/components/sort-field/sort-field.component"

@Component({ template: '' })
export abstract class ScoutDataTableBase<RecordType> extends TableViewComponentBase<RecordType> {

  @Input() set scoutTaskDoc ( doc: ScoutTaskDoc ) {
    this._scoutTaskDoc = doc
  }

  _scoutTaskDoc : ScoutTaskDoc
  tableQuerySubscription : Subscription
  scoutModal : ScoutPointRefDialogComponent
  selectedRecord : RecordType

  constructor(
      appState: AppState,
      elementRef: ElementRef,
      datePipe: DatePipe,
      public roleMgr: RoleMgr,
      private bsModalService: BsModalService
  ){
    super(appState,bsModalService,elementRef,datePipe)
    this.pageSize = 0 // no paging
  }

  createTableQuerySubscription() : Observable<any> {
    return this._scoutTaskDoc.get$('scoutPaths')
  }


  async processQueryResults(queryResults: any[]) {
    return queryResults
  }


  initialize() {
    this.load()
  }


  public onThisTabSelected = ( bSelected: boolean ) => {
    if ( bSelected ) {

    } else {
      this.tableQuerySubscription?.unsubscribe()
    }
    this.onTabSelected(bSelected)
  }


  showScoutPoint( scoutPath: ScoutPath, scoutPoint: ScoutPoint ) {
    let uiLocked = TaskStatus.uiLockHandler(scoutPath.statusId, this.roleMgr.canEditUsers)
    const context : ModalOptions = {
      initialState: {
        scoutPointId: scoutPoint.id,
        scoutPathId: scoutPath.id,
        scoutTaskDoc: this._scoutTaskDoc,
        allowEditMode: true,
        locked: uiLocked,
        hidePhotos: true
      },
      class: 'full-screen-modal',
      id: ScoutPointRefDialogComponent.ModalId,
      animated: true,
      ignoreBackdropClick: true
    }
    const modalRef = this.bsModalService.show(ScoutPointRefDialogComponent, context );
    this.scoutModal = modalRef.content
    const pointChangeSubscription = this.scoutModal.selectedPointChanged.subscribe(this.onScoutPointModalChange)
    this.scoutModal.onClosed.pipe(first()).subscribe( () => {
      this.scoutModal = null
      pointChangeSubscription?.unsubscribe()
    })
  }


  onScoutPointModalChange = ( pt:ScoutPoint ) => {}


  doQuery = async (queryReq: TableViewQueryRequest) => {

    const requestId = queryReq.requestId // super.load() expects this same value in response

    let bHaveFirstResult = false

    const firstResultPromise = new Promise<any>( async (completeFn, rejectFn )=>{
      this.tableQuerySubscription?.unsubscribe()
      this.tableQuerySubscription = this.createTableQuerySubscription().subscribe(
        async queryResults => {
          this.records = await this.processQueryResults(queryResults)
          //TODO add paging support
          if ( this.sortColumns?.length ) {
            const sortFns = this.sortColumns?.map(sortCol=> {
              const key = sortCol.key
              const tableColumn = this.tableColumns.find(tc=>tc.name==key)
              const ascSortFn = ['numeric','percent'].includes(tableColumn.type) ?
                (a,b)=>NumericCompareFn(a[key],b[key]) :
                (a,b)=>SortCompareFn(a[key],b[key])
              const sortFn = sortCol.order == 'ASC' ? ascSortFn : (a,b) => (ascSortFn(a,b)*-1) as 0|1|-1
              return sortFn
            })
            const compositSortFn = (a,b) => {
              for( const sortFn of sortFns ) {
                const rtn = sortFn(a,b)
                if ( rtn ) {
                  return rtn
                }
              }
              return 0
            }
            this.records.sort( (a,b) => compositSortFn(a,b) )
          }

          if ( !bHaveFirstResult ) {
            bHaveFirstResult = true
            completeFn( {
              requestId,
              tableRowCount: queryResults.length,
              queryRowCount: this.records.length,
              rows: this.records,
              pageNumber: queryReq.page?.number || 1,
              selectedIndices: []
            })
          } else {
            // This is an update from RXDB that the scout document has changed (time to check or redo points)
          }
        },
        error => {
          rejectFn(error)
        })
    })
    // RxDb subscription will continually listen for changes so treat first result like a traditional
    // database fetch because the table-view base class is not designed to listen for changes
    // after query runs
    return firstResultPromise
  }


  reload() {
    if ( this.filtersModified() ) {
      this.load()
    }
  }


  applyLocalFilter( record: RecordType) {
    for ( const filter of this.buildPopulatedFilters() ) {
      const value = record[filter.key]
      if ( filter.value || ['empty','not empty'].includes(filter.mode)) {
        const matcherFn = getMatcherForPattern(filter.value,filter.mode)
        if ( !matcherFn(value) ) {
          return false
        }
      }
    }
    return true
  }

  public download = async () =>  {
    this.bDownloading = true
    try {
      const ws: XLSX.WorkSheet=XLSX.utils.table_to_sheet(this.elementRef.nativeElement);  //TODO: remove colspan attribute from all elements
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, this.getName());
      var wbout = XLSX.write(wb, {type:"array", bookType:'xlsx'});
      const blob = new Blob([wbout], {type: 'application/octet-stream'})
      saveAs(blob, this.getName() + ".xlsx");
    } catch (error) {
      console.error(error)
      this.reportErrorFn(error)
    }
    this.bDownloading = false
  }

}